/**
 * Opengater mailings hooks (users service, `admin: mailings`): drafts, launch,
 * progress. The list and an open card poll while a mailing is scheduled or going
 * out; the audience count is a POST that saves nothing, kept per saved version.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiOg } from './client';
const PATH = '/api/v1/admin/mailings';
const KEY = ['og', 'mailings'];
const PAGE = 200;
/** Still moving on its own: waiting for its time, or going out. */
export const isLive = (status) => status === 'scheduled' || status === 'sending';
export function useOgMailings(status) {
    const query = new URLSearchParams({ limit: String(PAGE), offset: '0' });
    if (status)
        query.set('status', status);
    return useQuery({
        queryKey: [...KEY, 'list', status],
        queryFn: () => apiOg('users', `${PATH}?${query}`),
        refetchInterval: (q) => (q.state.data?.items.some((m) => isLive(m.status)) ? 10_000 : false),
    });
}
export function useOgMailing(id) {
    return useQuery({
        queryKey: [...KEY, 'card', id],
        queryFn: () => apiOg('users', `${PATH}/${id}`),
        enabled: id != null,
        refetchInterval: (q) => {
            const status = q.state.data?.status;
            return status === 'sending' ? 5_000 : status === 'scheduled' ? 15_000 : false;
        },
    });
}
/** Who it would go to now; asked again whenever the saved mailing changes. */
export function useOgMailingPreview(mailing, enabled) {
    return useQuery({
        queryKey: ['og', 'mailing-preview', mailing?.id, mailing?.updated_at],
        queryFn: () => apiOg('users', `${PATH}/${mailing.id}/preview`, { method: 'POST' }),
        enabled: enabled && mailing != null,
        staleTime: 60_000,
    });
}
/** A mailing the server answered with goes into the card's cache; the lists refresh. */
function useSettle() {
    const qc = useQueryClient();
    return (m) => {
        qc.setQueryData([...KEY, 'card', m.id], m);
        return qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    };
}
export function useCreateOgMailing() {
    const settle = useSettle();
    return useMutation({
        mutationFn: (body) => apiOg('users', PATH, { method: 'POST', body }),
        onSuccess: settle,
    });
}
export function useUpdateOgMailing(id) {
    const settle = useSettle();
    return useMutation({
        mutationFn: (patch) => apiOg('users', `${PATH}/${id}`, { method: 'PATCH', body: patch }),
        onSuccess: settle,
    });
}
export function useDeleteOgMailing(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => apiOg('users', `${PATH}/${id}`, { method: 'DELETE' }),
        onSuccess: () => {
            qc.removeQueries({ queryKey: [...KEY, 'card', id] });
            return qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
        },
    });
}
/** Launch now (null) or at a time — an ISO string with its offset. */
export function useSendOgMailing(id) {
    const settle = useSettle();
    return useMutation({
        mutationFn: (scheduledAt) => apiOg('users', `${PATH}/${id}/send`, {
            method: 'POST',
            body: scheduledAt ? { scheduled_at: scheduledAt } : {},
        }),
        onSuccess: settle,
    });
}
export function useCancelOgMailing(id) {
    const settle = useSettle();
    return useMutation({
        mutationFn: () => apiOg('users', `${PATH}/${id}/cancel`, { method: 'POST' }),
        onSuccess: settle,
    });
}
export function useStopOgMailing(id) {
    const settle = useSettle();
    return useMutation({
        mutationFn: () => apiOg('users', `${PATH}/${id}/stop`, { method: 'POST' }),
        onSuccess: settle,
    });
}
/** Sends the saved mailing in one language to the admin's own contacts. */
export function useTestOgMailing(id) {
    return useMutation({
        mutationFn: (language) => apiOg('users', `${PATH}/${id}/test`, {
            method: 'POST',
            body: { language },
        }),
    });
}
export async function uploadMailingMedia(file) {
    const form = new FormData();
    form.append('file', file);
    const res = await apiOg('users', `${PATH}/media`, {
        method: 'POST',
        body: form,
    });
    if (!res?.url || !res.media_type)
        throw new Error('Сервер не вернул адрес файла.');
    return res;
}
